import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	TConfirmedOrdersResponsePayload,
	TOrderDetailsRequest,
	TOrderDetailsResponsePayload,
	TOrderDiscountsRequest,
	TOrderDiscountsResponsePayload,
	TOrdersRequest,
	TOrdersStore,
	TReturnedOrdersRequest,
	TReturnedOrdersResponsePayload,
	TOrderReturnBreakdownRequest,
	TOrderReturnBreakdownResponsePayload,
	TOrderDiscountBreakdownRequest,
	TOrderDiscountBreakdownResponsePayload,
	TOrderVatCorrectionsBreakdownRequest,
	TOrderVatCorrectionsBreakdownResponsePayload,
	TOrderReturnItemsRequest,
	TOrderReturnItemsResponsePayload,
	TCreateOrderReturnActionPayload,
	TOrderDiscountItemsRequest,
	TOrderDiscountItemsResponsePayload,
	TCreateOrderDiscountActionPayload,
	TVatAndEASFeeResponsePayload,
	TOrderDetailsCombinedRequest,
	TOrderDetailsCombinedResponsePayload,
} from './types';
// ==========================================:
export const initialState: TOrdersStore = {
	confirmedOrders: null,
	confirmedOrdersLoader: false,
	confirmedOrdersForFilterId: null,
	confirmedOrdersForFilterIdLoader: false,
	returnedOrders: null,
	returnedOrdersLoader: false,
	orderDiscounts: null,
	orderDiscountsLoader: false,
	orderDetails: null,
	orderDetailsLoader: false,
	orderDetailsCombined: null,
	orderDetailsCombinedLoader: false,
	orderReturnBreakdown: null,
	orderReturnBreakdownLoader: false,
	orderDiscountBreakdown: null,
	orderDiscountBreakdownLoader: false,
	orderVatCorrectionsBreakdown: null,
	orderVatCorrectionsBreakdownLoader: false,
	orderReturnItems: null,
	orderReturnItemsLoader: false,
	createOrderReturnLoader: false,
	orderDiscountItems: null,
	orderDiscountItemsLoader: false,
	createOrderDiscountLoader: false,
	vatAndEASFee: null,
	vatAndEASFeeLoader: false,
};

// ==========================================:
const orders = createSlice({
	name: '@@orders',
	initialState,
	reducers: {
		getConfirmedOrdersRequest: (state: TOrdersStore, action: PayloadAction<TOrdersRequest>) => {
			const ordersState = state;
			ordersState.confirmedOrdersLoader = true;
		},
		getConfirmedOrdersSuccess: (
			state: TOrdersStore,
			action: PayloadAction<TConfirmedOrdersResponsePayload>,
		) => {
			const { payload } = action;
			const ordersState = state;
			ordersState.confirmedOrders = payload;
			ordersState.confirmedOrdersLoader = false;
		},
		getConfirmedOrdersForFilterIdRequest: (
			state: TOrdersStore,
			action: PayloadAction<TOrdersRequest>,
		) => {
			const ordersState = state;
			ordersState.confirmedOrdersForFilterIdLoader = true;
		},
		getConfirmedOrdersForFilterIdSuccess: (
			state: TOrdersStore,
			action: PayloadAction<TConfirmedOrdersResponsePayload>,
		) => {
			const { payload } = action;
			const ordersState = state;
			ordersState.confirmedOrdersForFilterId = payload;
			ordersState.confirmedOrdersForFilterIdLoader = false;
		},
		resetConfirmedOrdersForFilterId: (state: TOrdersStore) => {
			const ordersState = state;
			ordersState.confirmedOrdersForFilterId = null;
			ordersState.confirmedOrdersForFilterIdLoader = false;
		},
		getReturnedOrdersRequest: (
			state: TOrdersStore,
			action: PayloadAction<TReturnedOrdersRequest>,
		) => {
			const ordersState = state;
			ordersState.returnedOrdersLoader = true;
		},
		getReturnedOrdersSuccess: (
			state: TOrdersStore,
			action: PayloadAction<TReturnedOrdersResponsePayload>,
		) => {
			const { payload } = action;
			const ordersState = state;
			ordersState.returnedOrders = payload;
			ordersState.returnedOrdersLoader = false;
		},
		getOrderDiscountsRequest: (
			state: TOrdersStore,
			action: PayloadAction<TOrderDiscountsRequest>,
		) => {
			const ordersState = state;
			ordersState.orderDiscountsLoader = true;
		},
		getOrderDiscountsSuccess: (
			state: TOrdersStore,
			action: PayloadAction<TOrderDiscountsResponsePayload>,
		) => {
			const { payload } = action;
			const ordersState = state;
			ordersState.orderDiscounts = payload;
			ordersState.orderDiscountsLoader = false;
		},
		getOrderDetailsRequest: (state: TOrdersStore, action: PayloadAction<TOrderDetailsRequest>) => {
			const ordersState = state;
			ordersState.orderDetailsLoader = true;
		},
		getOrderDetailsSuccess: (
			state: TOrdersStore,
			action: PayloadAction<TOrderDetailsResponsePayload>,
		) => {
			const { payload } = action;
			const ordersState = state;
			ordersState.orderDetails = payload;
			ordersState.orderDetailsLoader = false;
		},
		getOrderDetailsCombinedRequest: (
			state: TOrdersStore,
			action: PayloadAction<TOrderDetailsCombinedRequest>,
		) => {
			const ordersState = state;
			ordersState.orderDetailsCombinedLoader = true;
		},
		getOrderDetailsCombinedSuccess: (
			state: TOrdersStore,
			action: PayloadAction<TOrderDetailsCombinedResponsePayload>,
		) => {
			const { payload } = action;
			const ordersState = state;
			ordersState.orderDetailsCombined = payload;
			ordersState.orderDetailsCombinedLoader = false;
		},
		getOrderReturnBreakdownRequest: (
			state: TOrdersStore,
			action: PayloadAction<TOrderReturnBreakdownRequest>,
		) => {
			const ordersState = state;
			ordersState.orderReturnBreakdownLoader = true;
		},
		getOrderReturnBreakdownSuccess: (
			state: TOrdersStore,
			action: PayloadAction<TOrderReturnBreakdownResponsePayload>,
		) => {
			const { payload } = action;
			const ordersState = state;
			ordersState.orderReturnBreakdown = payload;
			ordersState.orderReturnBreakdownLoader = false;
		},
		getOrderDiscountBreakdownRequest: (
			state: TOrdersStore,
			action: PayloadAction<TOrderDiscountBreakdownRequest>,
		) => {
			const ordersState = state;
			ordersState.orderDiscountBreakdownLoader = true;
		},
		getOrderDiscountBreakdownSuccess: (
			state: TOrdersStore,
			action: PayloadAction<TOrderDiscountBreakdownResponsePayload>,
		) => {
			const { payload } = action;
			const ordersState = state;
			ordersState.orderDiscountBreakdown = payload;
			ordersState.orderDiscountBreakdownLoader = false;
		},
		getOrderVatCorrectionsBreakdownRequest: (
			state: TOrdersStore,
			action: PayloadAction<TOrderVatCorrectionsBreakdownRequest>,
		) => {
			const ordersState = state;
			ordersState.orderVatCorrectionsBreakdownLoader = true;
		},
		getOrderVatCorrectionsBreakdownSuccess: (
			state: TOrdersStore,
			action: PayloadAction<TOrderVatCorrectionsBreakdownResponsePayload>,
		) => {
			const { payload } = action;
			const ordersState = state;
			ordersState.orderVatCorrectionsBreakdown = payload;
			ordersState.orderVatCorrectionsBreakdownLoader = false;
		},
		getOrderReturnItemsRequest: (
			state: TOrdersStore,
			action: PayloadAction<TOrderReturnItemsRequest>,
		) => {
			const ordersState = state;
			ordersState.orderReturnItemsLoader = true;
		},
		getOrderReturnItemsSuccess: (
			state: TOrdersStore,
			action: PayloadAction<TOrderReturnItemsResponsePayload>,
		) => {
			const { payload } = action;
			const ordersState = state;
			ordersState.orderReturnItems = payload;
			ordersState.orderReturnItemsLoader = false;
		},
		createOrderReturnRequest: (
			state: TOrdersStore,
			action: PayloadAction<TCreateOrderReturnActionPayload>,
		) => {
			const ordersState = state;
			ordersState.createOrderReturnLoader = true;
		},
		createOrderReturnSuccess: (state: TOrdersStore) => {
			const ordersState = state;
			ordersState.createOrderReturnLoader = false;
		},
		getOrderDiscountItemsRequest: (
			state: TOrdersStore,
			action: PayloadAction<TOrderDiscountItemsRequest>,
		) => {
			const ordersState = state;
			ordersState.orderDiscountItemsLoader = true;
		},
		getOrderDiscountItemsSuccess: (
			state: TOrdersStore,
			action: PayloadAction<TOrderDiscountItemsResponsePayload>,
		) => {
			const { payload } = action;
			const ordersState = state;
			ordersState.orderDiscountItems = payload;
			ordersState.orderDiscountItemsLoader = false;
		},
		createOrderDiscountRequest: (
			state: TOrdersStore,
			action: PayloadAction<TCreateOrderDiscountActionPayload>,
		) => {
			const ordersState = state;
			ordersState.createOrderDiscountLoader = true;
		},
		createOrderDiscountSuccess: (state: TOrdersStore) => {
			const ordersState = state;
			ordersState.createOrderDiscountLoader = false;
		},
		getVatAndEASFeeRequest: (state: TOrdersStore) => {
			const ordersState = state;
			ordersState.vatAndEASFeeLoader = true;
		},
		getVatAndEASFeeSuccess: (
			state: TOrdersStore,
			action: PayloadAction<TVatAndEASFeeResponsePayload>,
		) => {
			const { payload } = action;
			const ordersState = state;
			ordersState.vatAndEASFee = payload;
			ordersState.vatAndEASFeeLoader = false;
		},
		ordersInitState: () => initialState,
	},
});

export default orders.reducer;
export const {
	getConfirmedOrdersRequest,
	getConfirmedOrdersSuccess,
	getConfirmedOrdersForFilterIdRequest,
	getConfirmedOrdersForFilterIdSuccess,
	resetConfirmedOrdersForFilterId,
	getReturnedOrdersRequest,
	getReturnedOrdersSuccess,
	getOrderDiscountsRequest,
	getOrderDiscountsSuccess,
	getOrderVatCorrectionsBreakdownRequest,
	getOrderVatCorrectionsBreakdownSuccess,
	getOrderDetailsRequest,
	getOrderDetailsSuccess,
	getOrderDetailsCombinedRequest,
	getOrderDetailsCombinedSuccess,
	getOrderReturnBreakdownRequest,
	getOrderReturnBreakdownSuccess,
	getOrderDiscountBreakdownRequest,
	getOrderDiscountBreakdownSuccess,
	getOrderReturnItemsRequest,
	getOrderReturnItemsSuccess,
	createOrderReturnRequest,
	createOrderReturnSuccess,
	getOrderDiscountItemsRequest,
	getOrderDiscountItemsSuccess,
	createOrderDiscountRequest,
	createOrderDiscountSuccess,
	getVatAndEASFeeRequest,
	getVatAndEASFeeSuccess,
	ordersInitState,
} = orders.actions;
